import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardHeader, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Plus, Search } from "lucide-react";
import EditAssetModal from "../components/assets/EditAssetModal";
import { usePermissions } from "../context/PermissionsContext";
import usePagedList from "../hooks/usePagedList";
import PaginationBar from "../components/ui/PaginationBar";
import ExportXlsxButton from "../components/ui/ExportXlsxButton";

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const navigate = useNavigate();
  const { can } = usePermissions();
  const pg = usePagedList(assets);

  const fetchAssets = async () => {
    try {
      setLoading(true);

      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get("/assets/", { params });
      setAssets(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar ativos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [search, statusFilter]);

  const openEdit = (asset, e) => {
    if (e) e.stopPropagation();
    setSelectedAsset(asset);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setSelectedAsset(null);
    setIsEditOpen(false);
  };

  const handleEditSubmit = (formData) => {
    if (!selectedAsset?.id) return;

    const assetId = selectedAsset.id;

    const payload = {
      asset_type: formData.asset_type,
      brand: formData.brand,
      model: formData.model,
      hostname: formData.hostname,
      serial_number: formData.serial_number,
      patrimony_code: formData.patrimony_code,
      invoice_number: formData.invoice_number,
      status: formData.status,
      location: formData.location,
      collaborator_id: formData.collaborator_id ? Number(formData.collaborator_id) : null,
      notes: formData.notes,
    };

    closeEdit();

    api
      .put(`/assets/${assetId}`, payload)
      .then(() => fetchAssets())
      .catch((error) => {
        console.error("Erro ao atualizar ativo:", error);
        alert("Erro ao atualizar ativo.");
      });
  };

  const handleDelete = (assetId) => {
    closeEdit();

    api
      .delete(`/assets/${assetId}`)
      .then(() => fetchAssets())
      .catch((error) => {
        console.error("Erro ao deletar ativo:", error);
        alert("Erro ao deletar ativo.");
      });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      Ativo: "bg-green-100 text-green-800",
      "Em Estoque": "bg-blue-100 text-blue-800",
      Inativo: "bg-gray-100 text-gray-800",
      "Em Manutenção": "bg-yellow-100 text-yellow-800",
      Manutenção: "bg-yellow-100 text-yellow-800",
      Baixado: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const getTypeLabel = (a) => a.asset_type || a.type || a.asset_type_name || "—";
  const getBrandModel = (a) =>
    [a.brand, a.model].filter(Boolean).join(" ") || a.brand_model || "—";
  const getCollaboratorLabel = (a) => a.collaborator_name || "Não vinculado";

  return (
    <Layout>
      <div className="space-y-6" data-testid="assets-page">
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ativos</h1>
            <p className="text-gray-500">Gerencie equipamentos e ativos</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <ExportXlsxButton url="/assets/export" filename="ativos.xlsx" />
            {can("create", "/assets") && (
              <Button onClick={() => navigate("/assets/new")}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Ativo
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar ativos..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos os Status</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Em Estoque">Em Estoque</option>
                  <option value="Inativo">Inativo</option>
                  <option value="Em Manutenção">Em Manutenção</option>
                  <option value="Baixado">Baixado</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : assets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum ativo encontrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Tipo</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Marca/Modelo</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 hidden lg:table-cell">Colaborador</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Número de Série</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Localização</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pg.pageItems.map((asset) => (
                      <tr
                        key={asset.id}
                        onClick={() => navigate(`/assets/${asset.id}`)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-gray-900">{getTypeLabel(asset)}</td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          <div>{getBrandModel(asset)}</div>
                          <div className="text-xs text-gray-500 lg:hidden truncate max-w-[180px]" title={getCollaboratorLabel(asset)}>
                            {getCollaboratorLabel(asset)}
                          </div>
                        </td>
                        <td
                          className="py-3 px-4 text-sm text-gray-600 hidden lg:table-cell max-w-[220px] truncate"
                          title={getCollaboratorLabel(asset)}
                        >
                          {getCollaboratorLabel(asset)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{asset.serial_number || "—"}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                              asset.status
                            )}`}
                          >
                            {asset.status || "—"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{asset.location || "N/A"}</td>
                        <td className="py-3 px-4 text-right text-sm">
                          <Button
                            variant="outline"
                            onClick={(e) => openEdit(asset, e)}
                            className="h-auto px-3 py-1 text-xs text-gray-700"
                          >
                            Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationBar
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.onPageChange}
                  onPageSizeChange={pg.onPageSizeChange}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <EditAssetModal
          isOpen={isEditOpen}
          onClose={closeEdit}
          asset={selectedAsset}
          onSubmit={handleEditSubmit}
          onDelete={handleDelete}
        />
      </div>
    </Layout>
  );
};

export default Assets;
